import React, { useEffect, useState, useCallback } from "react";
import { api, uploadFile } from "../../api/client";
import ConfirmModal from "../../components/ConfirmModal";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}

const DOCS = "/uploads/documents/";

export default function ConseilAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // objet conseil ou {} pour nouveau
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/admin/conseils")).items || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    try {
      await api.del(`/admin/conseils/${toDelete.id}`);
      setToDelete(null); load();
    } catch (e) { alert("Erreur : " + e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.6rem" }}>
        <p className="muted" style={{ margin: 0 }}>
          Comptes rendus (PV) des séances du conseil municipal.
        </p>
        <button onClick={() => setEditing({})}>+ Nouveau compte rendu</button>
      </div>

      {loading && <p className="muted mt-2">Chargement…</p>}
      {!loading && items.length === 0 &&
        <p className="muted mt-2">Aucun compte rendu enregistré.</p>}

      {items.length > 0 && (
        <table className="admin-table mt-2"><thead><tr>
          <th>Titre</th><th>Séance</th><th>Publication</th>
          <th>PDF</th><th>Actions</th>
        </tr></thead><tbody>
          {items.map((c) => (
            <tr key={c.id}>
              <td>{c.titre}</td>
              <td className="muted">{formatDate(c.date_conseil)}</td>
              <td className="muted">{formatDate(c.date_publication)}</td>
              <td>
                <a href={DOCS + c.filename} target="_blank" rel="noopener noreferrer">
                  Voir
                </a>
              </td>
              <td>
                <button className="small" onClick={() => setEditing(c)}>Modifier</button>
                <button className="small danger" style={{ marginLeft: 6 }}
                        onClick={() => setToDelete(c)}>Supprimer</button>
              </td>
            </tr>
          ))}
        </tbody></table>
      )}

      {editing && (
        <ConseilEditor item={editing}
                       onClose={() => setEditing(null)}
                       onSaved={() => { setEditing(null); load(); }} />
      )}
      {toDelete && (
        <ConfirmModal title="Supprimer ce compte rendu ?"
                      message={`« ${toDelete.titre} » et son PDF seront définitivement supprimés.`}
                      confirmLabel="Supprimer" variant="danger"
                      onConfirm={confirmDelete} onCancel={() => setToDelete(null)} />
      )}
    </div>
  );
}

function ConseilEditor({ item, onClose, onSaved }) {
  const isNew = !item.id;
  const today = new Date().toISOString().slice(0, 10);
  const [titre, setTitre] = useState(item.titre || "");
  const [dateConseil, setDateConseil] = useState((item.date_conseil || "").slice(0, 10));
  const [datePublication, setDatePublication] = useState(
    (item.date_publication || today).slice(0, 10));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!titre.trim()) { setError("Le titre est obligatoire."); return; }
    if (!dateConseil) { setError("La date de séance est obligatoire."); return; }
    if (isNew && !file) { setError("Sélectionnez le PV au format PDF."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      fd.append("titre", titre.trim());
      fd.append("date_conseil", dateConseil);
      fd.append("date_publication", datePublication);
      if (isNew) {
        await uploadFile("/admin/conseils", fd);
      } else {
        // PUT multipart : on passe par fetch via uploadFile-like.
        // uploadFile fait un POST ; pour le PUT on utilise api avec FormData natif.
        await putForm(`/admin/conseils/${item.id}`, fd);
      }
      onSaved();
    } catch (e) { setError(e.message || "Échec de l'enregistrement"); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>{isNew ? "Nouveau compte rendu" : "Modifier le compte rendu"}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="field"><label>Titre *</label>
            <input value={titre} onChange={(e) => setTitre(e.target.value)}
                   maxLength={200} placeholder="Ex. : Conseil municipal du 15 mars 2026" /></div>
          <div className="row">
            <div className="field"><label>Date de la séance *</label>
              <input type="date" value={dateConseil}
                     onChange={(e) => setDateConseil(e.target.value)} /></div>
            <div className="field"><label>Date de publication</label>
              <input type="date" value={datePublication}
                     onChange={(e) => setDatePublication(e.target.value)} /></div>
          </div>
          <div className="field">
            <label>{isNew ? "Fichier PDF *" : "Remplacer le PDF (optionnel)"}</label>
            <input type="file" accept=".pdf"
                   onChange={(e) => setFile(e.target.files[0] || null)} />
            {!isNew && (
              <span className="muted" style={{ fontSize: "0.82em" }}>
                Laissez vide pour conserver le PDF actuel.
              </span>
            )}
          </div>
          {error && <div className="error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="secondary" onClick={onClose}>Annuler</button>
          <button onClick={submit} disabled={busy}>
            {busy ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper PUT multipart (le client.js ne gere que GET/PUT JSON et POST multipart).
async function putForm(path, formData) {
  const base = process.env.REACT_APP_API_BASE || "/api";
  const token = localStorage.getItem("mairie_token") || "";
  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${base}${path}`, {
    method: "PUT", headers, body: formData, credentials: "include",
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || res.statusText);
  }
  return res.json();
}
